/**
* IBM Confidential
* Licensed Materials - Property of IBM
* (C) Copyright IBM Corp. 2020, 2024 All Rights Reserved.
* US Government Users Restricted Rights - Use, duplication or
* disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
* */

import { objectWithoutProperties as _objectWithoutProperties, slicedToArray as _slicedToArray, extends as _extends } from '../../../_virtual/_rollupPluginBabelHelpers.js';
import classnames from 'classnames';
import PropTypes from 'prop-types';
import React, { useState, useEffect } from 'react';
import { useTranslation } from '../../../Utilities/i18n/i18n.js';
import { noopFn } from '../../../Utilities/propHelpers/defaultProps.js';
import NavCollapseButton from './children/NavCollapseButton.js';
import NavHeader from './children/NavHeader.js';
import NavItems from './children/NavItems.js';
import { findActiveItem as _findActiveItem } from './utils/findActiveItem.js';

var _excluded = ["activeHref", "showMenuItems", "className", "collapseButtonLocation", "inverse", "items", "linkComponent", "onToggle", "open", "showHeader", "title"];
var selectionKeys = ['Enter', ' ', 'Spacebar'];

/**
 * Checks whether the event type was a click event or the user seleted with enter or space.
 * @param {object} evt An event object.
 */
var isSelectEvent = function isSelectEvent(evt) {
  return evt.type === 'click' || selectionKeys.includes(evt.key);
};

/**
 * Returns the current pathname, so long as there is a window.
 */
var getWindowPath = function getWindowPath() {
  if (typeof window !== 'undefined') {
    var _window;
    return ((_window = window) === null || _window === void 0 || (_window = _window.location) === null || _window === void 0 ? void 0 : _window.pathname) || '';
  }
  return '';
};

/**
 * Returns the initial href to set as active.
 */
var getInitialActiveHref = function getInitialActiveHref(active) {
  return active || getWindowPath();
};
var SideNav = function SideNav(_ref) {
  var activeOverride = _ref.activeHref,
    _ref$showMenuItems = _ref.showMenuItems,
    showMenuItems = _ref$showMenuItems === void 0 ? true : _ref$showMenuItems,
    className = _ref.className,
    _ref$collapseButtonLo = _ref.collapseButtonLocation,
    collapseButtonLocation = _ref$collapseButtonLo === void 0 ? 'top' : _ref$collapseButtonLo,
    inverse = _ref.inverse,
    items = _ref.items,
    _ref$linkComponent = _ref.linkComponent,
    LinkComponent = _ref$linkComponent === void 0 ? 'a' : _ref$linkComponent,
    _ref$onToggle = _ref.onToggle,
    onToggle = _ref$onToggle === void 0 ? noopFn : _ref$onToggle,
    _ref$open = _ref.open,
    initialOpen = _ref$open === void 0 ? true : _ref$open,
    showHeader = _ref.showHeader,
    title = _ref.title,
    rest = _objectWithoutProperties(_ref, _excluded);
  var _useTranslation = useTranslation(),
    t = _useTranslation.t;
  var initialActive = getInitialActiveHref(activeOverride);

  // Component state
  var _useState = useState(initialOpen),
    _useState2 = _slicedToArray(_useState, 2),
    open = _useState2[0],
    setOpen = _useState2[1];
  var _useState3 = useState(initialActive),
    _useState4 = _slicedToArray(_useState3, 2),
    activeHref = _useState4[0],
    setActiveHref = _useState4[1];
  var collapseButtonLabel = open ? t('Close page navigation menu') : t('Open page navigation menu');
  var activeItem = _findActiveItem(items, activeHref);

  // Update the active href if this changes at all.
  useEffect(function () {
    if (activeOverride) {
      setActiveHref(activeOverride);
    }
  }, [activeOverride]);
  useEffect(function () {
    return setOpen(initialOpen);
  }, [initialOpen]);

  // Keyboard events
  var onSelect = function onSelect(evt, href, callBack) {
    var selectEvent = isSelectEvent(evt);
    // Only set the active href in the event that an override is not provided.
    if (selectEvent && !activeOverride && href !== activeHref) {
      setActiveHref(href);
    }
    if (selectEvent && href !== activeHref && collapseButtonLocation === 'top') setOpen(false);
    if (callBack) {
      callBack(evt, href);
    }
  };
  var collapseButton = /*#__PURE__*/React.createElement(NavCollapseButton, {
    activeLabel: activeItem && collapseButtonLocation === 'top' ? /*#__PURE__*/React.createElement("div", {
      className: "cpc--side-nav__collapse-button__label"
    }, title && /*#__PURE__*/React.createElement("span", {
      className: "cds--label"
    }, title), activeItem.label) : /*#__PURE__*/React.createElement("div", {
      className: "cpc--side-nav__collapse-button__label-mobile"
    }, /*#__PURE__*/React.createElement("span", {
      className: "cpc--side-nav__collapse-button__label-inactive-mobile"
    }, collapseButtonLabel)),
    collapseButtonLocation: collapseButtonLocation,
    onClick: function onClick(e) {
      var nextOpen = !open;
      onToggle(e, nextOpen);
      setOpen(nextOpen);
    },
    label: collapseButtonLabel,
    open: open
  });
  return /*#__PURE__*/React.createElement("nav", _extends({
    "aria-label": t('Page navigation menu'),
    className: classnames('cpc--side-nav', {
      'cpc--side-nav--collapsed': !open,
      'cpc--side-nav--inverse': inverse
    }, className)
    // eslint-disable-next-line react/jsx-props-no-spreading
  }, rest), collapseButtonLocation === 'top' && collapseButton, /*#__PURE__*/React.createElement(NavHeader, {
    title: title,
    showHeader: showHeader
  }), /*#__PURE__*/React.createElement(NavItems, {
    activeHref: activeHref,
    showMenuItems: showMenuItems,
    items: items,
    linkComponent: LinkComponent,
    onSelect: onSelect
  }), collapseButtonLocation === 'right' && collapseButton);
};
SideNav.propTypes = {
  /**
   * The href for the page the user is currently visiting. If this is not provided it will
   * attempt to get the location off of the windows pathname. Passing this prop in makes the component's
   * active state fully controlled by the prop.
   */
  activeHref: PropTypes.string,
  /**
   * A custom class name to be applied to the Nav's `nav` element.
   */
  className: PropTypes.string,
  /**
   * Whether or not the side nav should be collapsible.
   */
  collapseButtonLocation: PropTypes.oneOf(['top', 'right']),
  /**
   * Whether or not the side nav should render the inverse color theme (g90).
   */
  inverse: PropTypes.bool,
  /**
   * The nav items to render in the side nav.
   */
  items: PropTypes.arrayOf(PropTypes.shape({
    to: PropTypes.string,
    href: PropTypes.string,
    icon: PropTypes.shape({}),
    label: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
    open: PropTypes.bool,
    items: PropTypes.arrayOf(PropTypes.shape({
      to: PropTypes.string,
      href: PropTypes.string,
      label: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
      flyoutItems: PropTypes.arrayOf(PropTypes.shape({
        to: PropTypes.string,
        href: PropTypes.string,
        label: PropTypes.oneOfType([PropTypes.string, PropTypes.node])
      }))
    }))
  })).isRequired,
  /**
   * A component or element to use for the link element. If you're using something like React Router this can be a helpful backdoor to pass
   * in your own component, so the page doesn't re-render.
   */
  linkComponent: PropTypes.elementType,
  /**
   * A callback function to call when the Side Nav toggle button is clicked.
   */
  onToggle: PropTypes.func,
  /**
   * If side nav should be open or not.
   */
  open: PropTypes.bool,
  /**
   * Whether or not the header should be shown in the side nav.
   */
  showHeader: PropTypes.bool,
  /**
   * Whether or not menu items are allowed in the side nav.
   */
  showMenuItems: PropTypes.bool,
  /**
   * The title to be rendered at the top of the side nav component.
   */
  title: PropTypes.oneOfType([PropTypes.string, PropTypes.node])
};
var SideNav$1 = SideNav;

export { SideNav$1 as default };
